import { router } from "@inertiajs/react"
import {
  Calendar,
  ChevronLeft,
  Clock,
  MessageCircle,
  PhoneOff,
  Repeat,
} from "lucide-react"
import { useState } from "react"

import {
  type Channel,
  channelIcons,
  channelLabels,
} from "@/components/conversation-channel"
import { CopyButton } from "@/components/copy-button"
import {
  DrawerHeader,
  DrawerSheet,
  DrawerStrip,
  DrawerStripButton,
} from "@/components/drawer-sheet"
import { RecordingPlayer } from "@/components/recording-player"
import { Button } from "@/components/ui/button"
import { Empty } from "@/components/ui/empty"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"
import {
  pauseLeadOutreachAgentPath,
  resumeLeadOutreachAgentPath,
} from "@/routes"

// IMP-521: the contact drawer. The frame is the person — name, Lead badge,
// phone, outreach cycle — and never moves; the body shows the conversation
// that was clicked, with the contact's other conversations behind a list view.

export interface TranscriptLine {
  speaker: "agent" | "visitor"
  text: string
}

export interface ConversationDetail {
  id: string
  channel: Channel
  day: string
  time: string
  duration: string | null
  attempt: string | null
  // IMP-536: why the assistant hung up, when it did so deliberately.
  ended: string | null
  summary: string | null
  message: { body: string; taken_at: string | null } | null
  recording_url: string | null
  transcript: TranscriptLine[]
}

export interface ContactListRow {
  id: string
  channel: Channel
  day: string
  time: string
  summary: string | null
}

export interface CycleInfo {
  attempt: number
  of: number
  paused: boolean
  next: string | null
  agent_uid: string
  lead_uid: string | null
}

export interface ContactFrame {
  name: string | null
  phone: string | null
  lead: boolean
  cycle: CycleInfo | null
  conversations: ContactListRow[]
}

export interface SelectedConversation {
  conversation: ConversationDetail
  contact: ContactFrame
}

function LeadBadge() {
  return (
    <span className="inline-flex shrink-0 items-center rounded-md border border-blue-200 bg-blue-100 px-1.5 py-0.5 text-xs font-medium text-blue-700 dark:border-blue-400/20 dark:bg-blue-400/10 dark:text-blue-400">
      Lead
    </span>
  )
}

function CampaignStrip({ cycle }: { cycle: CycleInfo }) {
  const [busy, setBusy] = useState(false)

  function toggle() {
    if (!cycle.lead_uid || busy) return
    const path = cycle.paused
      ? resumeLeadOutreachAgentPath
      : pauseLeadOutreachAgentPath
    setBusy(true)
    router.post(
      path({ id: cycle.agent_uid }),
      { lead_uid: cycle.lead_uid },
      {
        preserveScroll: true,
        preserveState: true,
        onFinish: () => setBusy(false),
      },
    )
  }

  const detail = cycle.paused ? (
    <p className="text-[13px] font-medium text-amber-600 dark:text-amber-500">
      Paused — no further attempts scheduled
    </p>
  ) : cycle.next ? (
    <p className="text-muted-foreground text-[13px]">
      Next attempt {cycle.next}
    </p>
  ) : (
    <p className="text-muted-foreground text-[13px]">
      No further attempts scheduled
    </p>
  )

  return (
    <DrawerStrip
      leading={
        <div
          className="flex shrink-0 items-center gap-1"
          aria-label={`${cycle.attempt} of ${cycle.of} attempts`}
        >
          {Array.from({ length: cycle.of }, (_, i) => (
            <span
              key={i}
              className={cn(
                "h-1.5 w-5 rounded-full",
                i < cycle.attempt ? "bg-blue-500" : "bg-border",
              )}
            />
          ))}
        </div>
      }
      title={`${cycle.attempt} of ${cycle.of} contact attempts`}
      detail={detail}
      action={
        cycle.lead_uid ? (
          <DrawerStripButton onClick={toggle}>
            {cycle.paused ? "Resume" : "Pause"}
          </DrawerStripButton>
        ) : undefined
      }
    />
  )
}

function ConversationMeta({
  conversation,
  onBack,
}: {
  conversation: ConversationDetail
  onBack: () => void
}) {
  const Icon = channelIcons[conversation.channel]
  return (
    <div className="space-y-5">
      {/* Always offered, even for a single conversation — the way out
          shouldn't appear and disappear. */}
      <Button
        variant="ghost"
        size="sm"
        onClick={onBack}
        className="text-muted-foreground -ml-3 h-8 cursor-pointer text-[13px]"
      >
        <ChevronLeft className="size-3.5 shrink-0" />
        All conversations
      </Button>
      <div className="space-y-1.5">
        <p className="flex items-center gap-2 text-sm font-medium">
          <Icon className="size-3.5 shrink-0" />
          {channelLabels[conversation.channel]}
        </p>
        <div className="text-muted-foreground flex flex-wrap items-center gap-x-3.5 gap-y-1 text-sm">
          <span className="flex items-center gap-1.5">
            <Calendar className="size-3.5" />
            {conversation.day} at {conversation.time}
          </span>
          {conversation.duration && (
            <span className="flex items-center gap-1.5">
              <Clock className="size-3.5" />
              {conversation.duration}
            </span>
          )}
          {conversation.attempt && (
            <span className="flex items-center gap-1.5">
              <Repeat className="size-3.5" />
              {conversation.attempt}
            </span>
          )}
        </div>
      </div>
      {/* IMP-536: a call the assistant ended on purpose says so before the
          summary — it changes how the summary should be read. */}
      {conversation.ended && (
        <p className="text-muted-foreground flex items-start gap-1.5 pt-1 text-sm">
          <PhoneOff className="mt-0.5 size-3.5 shrink-0" />
          {conversation.ended}
        </p>
      )}
      {/* Unlabelled: sitting directly under the conversation's facts, the
          summary reads as the answer to "what happened". */}
      {conversation.summary && (
        <p className="pt-1 text-sm leading-relaxed">{conversation.summary}</p>
      )}
    </div>
  )
}

function ConversationBody({
  conversation,
}: {
  conversation: ConversationDetail
}) {
  return (
    <div className="space-y-6">
      {conversation.message && (
        <section className="space-y-2.5">
          <Label>Message</Label>
          <div className="bg-muted/50 rounded-lg border p-3">
            <p className="text-sm leading-relaxed">
              {conversation.message.body}
            </p>
          </div>
        </section>
      )}

      {conversation.recording_url && (
        <section className="space-y-2.5">
          <Label>Recording</Label>
          <RecordingPlayer
            src={conversation.recording_url}
            duration={conversation.duration ?? undefined}
            className="bg-background flex w-full items-center gap-2.5 rounded-lg border py-1.5 pr-1.5 pl-2.5"
          />
        </section>
      )}

      <section className="space-y-2.5">
        <Label>Transcript</Label>
        {conversation.transcript.length === 0 ? (
          <Empty
            icon={<MessageCircle className="h-5 w-5" />}
            title="No transcript"
          />
        ) : (
          <div className="space-y-2">
            {conversation.transcript.map((line, index) => (
              <div
                key={index}
                className={cn(
                  "flex gap-3",
                  line.speaker === "visitor" && "flex-row-reverse",
                )}
              >
                <div
                  className={cn(
                    "max-w-[78%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed",
                    line.speaker === "agent"
                      ? "bg-muted text-foreground rounded-tl-sm"
                      : // The same blue the switches and campaign pips use,
                        // rather than `primary`, which renders near-black.
                        "rounded-tr-sm bg-blue-500 text-white",
                  )}
                >
                  {line.text}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

function ConversationList({
  rows,
  onSelect,
}: {
  rows: ContactListRow[]
  onSelect: (id: string) => void
}) {
  return (
    // Ghost rows like the feed's — no dividers, and no persistent wash on the
    // conversation you arrived from: filled with the same color as hover, it
    // read as a stuck hover, not a state.
    <div className="flex flex-col gap-1">
      {rows.map((row) => {
        const Icon = channelIcons[row.channel]
        return (
          <button
            key={row.id}
            type="button"
            onClick={() => onSelect(row.id)}
            // Wider than the text column but inset from the sheet's edges, so
            // the hover wash never sits flush against the drawer border.
            className="hover:bg-muted/40 -mx-3 flex w-[calc(100%+1.5rem)] cursor-pointer items-start gap-3 rounded-xl px-3 py-2.5 text-left transition-colors"
          >
            <div className="min-w-0 flex-1">
              {/* Channel left, time flushed right — same first-line anatomy
                  as the feed rows. */}
              <div className="flex items-baseline justify-between gap-4">
                <span className="flex items-center gap-x-2">
                  <Icon className="text-muted-foreground size-3.5 shrink-0" />
                  <span className="text-sm font-medium">
                    {channelLabels[row.channel]}
                  </span>
                </span>
                <span className="text-muted-foreground shrink-0 text-xs">
                  {row.day} at {row.time}
                </span>
              </div>
              {row.summary && (
                <p className="text-muted-foreground mt-1.5 line-clamp-2 text-sm">
                  {row.summary}
                </p>
              )}
            </div>
          </button>
        )
      })}
    </div>
  )
}

function DrawerBody({
  selected,
  onSelect,
}: {
  selected: SelectedConversation
  onSelect: (id: string) => void
}) {
  const { conversation, contact } = selected
  const [listOpen, setListOpen] = useState(false)

  const displayName = contact.name ?? contact.phone ?? "Anonymous visitor"

  return (
    <>
      <DrawerHeader
        title={displayName}
        badge={contact.lead ? <LeadBadge /> : undefined}
        subtitle={
          contact.name && contact.phone ? (
            <span className="text-foreground/80 flex items-center gap-1.5 text-sm">
              {contact.phone}
              <CopyButton value={contact.phone} label="Copy phone number" />
            </span>
          ) : undefined
        }
        strip={
          contact.cycle ? <CampaignStrip cycle={contact.cycle} /> : undefined
        }
        className="border-b"
      />

      <div className="flex-1 overflow-y-auto px-6 pt-5 pb-5">
        {listOpen ? (
          <div className="space-y-4">
            <p className="text-sm font-medium">Conversations</p>
            <ConversationList
              rows={contact.conversations}
              onSelect={(id) => {
                setListOpen(false)
                if (id !== conversation.id) onSelect(id)
              }}
            />
          </div>
        ) : (
          <div className="space-y-8">
            <ConversationMeta
              conversation={conversation}
              onBack={() => setListOpen(true)}
            />
            <ConversationBody conversation={conversation} />
          </div>
        )}
      </div>
    </>
  )
}

export function ContactDrawer({
  selected,
  open,
  onClose,
  onSelect,
}: {
  selected: SelectedConversation | null
  open: boolean
  onClose: () => void
  onSelect: (id: string) => void
}) {
  return (
    <DrawerSheet open={open} onOpenChange={(o) => !o && onClose()}>
      {selected && (
        // Keyed on the conversation so switching from the list view remounts
        // the body (fresh scroll, fresh list state) while the sheet stays put.
        <DrawerBody
          key={selected.conversation.id}
          selected={selected}
          onSelect={onSelect}
        />
      )}
    </DrawerSheet>
  )
}
